import React, { useState, useEffect, useMemo } from 'react';
import { CheckSquare, X, Package, Wrench, Droplets, ChevronDown, ChevronUp, Search, Layers, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { RMLibrarySelector } from '../RMLibrarySelector';
import fetchWithAuth from '../../services/fetchWithAuth';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('access_token');

/* ------------------------------------------------------------------ */
/* Process Library Search — reusable for Fabrication & Surface         */
/* ------------------------------------------------------------------ */
const ProcessSearchSection = ({ domain, icon, title, color, borderColor, bgColor, makeLines, bomId, revisionId, onRefresh, onBusy }) => {
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [applying, setApplying] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [selected, setSelected] = useState(new Map()); // process_code → process object

  useEffect(() => {
    if (!expanded) return;
    (async () => {
      setLoading(true);
      const tk = getToken();
      try {
        const res = await fetchWithAuth(`${API}/api/v1/process-library/processes?domain=${domain}&status=active&limit=200`, { headers: { Authorization: `Bearer ${tk}` } });
        if (res.ok) { const d = await res.json(); setProcesses(d.items || d || []); }
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, [expanded, domain]);

  const filtered = processes.filter(p =>
    p.process_name?.toLowerCase().includes(search.toLowerCase()) ||
    p.process_code?.toLowerCase().includes(search.toLowerCase()) ||
    p.process_category?.toLowerCase().includes(search.toLowerCase())
  );

  const toggleSelect = (p) => {
    setSelected(prev => {
      const next = new Map(prev);
      if (next.has(p.process_code)) next.delete(p.process_code);
      else next.set(p.process_code, p);
      return next;
    });
  };

  const handleApplySelected = async () => {
    if (selected.size === 0 || makeLines.length === 0) return;
    setApplying(true);
    onBusy?.('applying');
    const tk = getToken();
    const lineIds = makeLines.map(l => l.line_id);
    let totalApplied = 0;
    let totalFailed = 0;

    for (const [code] of selected) {
      try {
        const res = await fetchWithAuth(`${API}/api/v1/process-library/batch-apply-to-lines`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
          body: JSON.stringify({
            process_code: code,
            bom_id: bomId,
            revision_id: revisionId,
            line_ids: lineIds,
          }),
        });
        if (res.ok) {
          const result = await res.json();
          totalApplied += result.applied || 0;
          totalFailed += result.failed || 0;
        } else { totalFailed += lineIds.length; }
      } catch { totalFailed += lineIds.length; }
    }

    toast.success(`Applied ${selected.size} ${isFab ? 'operation' : 'treatment'}${selected.size !== 1 ? 's' : ''} to ${makeLines.length} part${makeLines.length !== 1 ? 's' : ''}`);
    setSelected(new Map());
    await onRefresh();
    setApplying(false);
    onBusy?.(null);
  };

  const costDisplay = (p) => {
    const cm = p.cost_model || {};
    const rate = cm.rate || 0;
    const label = cm.unit_label || 'unit';
    return rate > 0 ? `${rate}/${label}` : '';
  };

  const isFab = domain === 'fabrication';

  return (
    <div className={`border ${borderColor} rounded-lg p-3 ${bgColor}`}>
      <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className={`text-[10px] ${color} font-semibold uppercase tracking-wider flex items-center gap-1`}>
          {icon} {title}
          <span className="opacity-60 ml-1">({makeLines.length} parts)</span>
        </div>
        {expanded ? <ChevronUp className="w-3 h-3 text-slate-500" /> : <ChevronDown className="w-3 h-3 text-slate-500" />}
      </div>
      {expanded && (
        <div className="mt-2 space-y-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-500" />
            <Input value={search} onChange={e => setSearch(e.target.value)}
              placeholder={`Search ${isFab ? 'operations' : 'treatments'}...`}
              className="h-7 text-[10px] bg-slate-800 border-slate-600 text-white pl-7" />
          </div>
          <div className="max-h-[200px] overflow-y-auto space-y-1">
            {loading ? (
              <div className="text-[10px] text-slate-500 text-center py-3">Loading...</div>
            ) : filtered.length === 0 ? (
              <div className="text-[10px] text-slate-500 text-center py-3">
                {processes.length === 0 ? `No ${domain} processes in library.` : 'No matching processes'}
              </div>
            ) : (
              filtered.map(p => {
                const isChecked = selected.has(p.process_code);
                return (
                  <button key={p.process_code} onClick={() => toggleSelect(p)}
                    className={`w-full text-left px-2 py-1.5 rounded transition-colors flex items-center gap-2 ${
                      isChecked
                        ? (isFab ? 'bg-blue-900/40 border border-blue-600/40' : 'bg-violet-900/40 border border-violet-600/40')
                        : 'hover:bg-slate-700/30 border border-transparent'
                    }`}
                    data-testid={`batch-proc-${p.process_code}`}>
                    <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 transition-colors ${
                      isChecked
                        ? (isFab ? 'bg-blue-600 border-blue-500' : 'bg-violet-600 border-violet-500')
                        : 'border-slate-600 bg-slate-800'
                    }`}>
                      {isChecked && <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] text-slate-200 font-medium truncate">{p.process_name}</span>
                        <span className="text-[9px] text-emerald-400/60 font-mono ml-2 shrink-0">{costDisplay(p)}</span>
                      </div>
                      <div className="text-[9px] text-slate-500 flex items-center gap-2">
                        <span>{p.process_code}</span>
                        {p.process_category && <span className="opacity-60">{p.process_category}</span>}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Selection Summary */}
          {selected.size > 0 && (
            <div className={`border ${borderColor} rounded-md p-2 bg-slate-800/40`} data-testid={`batch-${domain}-selection-summary`}>
              <div className={`text-[9px] ${color} font-semibold uppercase tracking-wider mb-1`}>
                Selected ({selected.size})
              </div>
              <div className="space-y-0.5 max-h-[60px] overflow-y-auto">
                {[...selected.values()].map(p => (
                  <div key={p.process_code} className="flex items-center justify-between text-[10px]">
                    <span className="text-slate-300 truncate">{p.process_name}</span>
                    <button onClick={(e) => { e.stopPropagation(); toggleSelect(p); }}
                      className="text-slate-600 hover:text-red-400 transition-colors ml-1 shrink-0">
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Apply Button */}
          {selected.size > 0 && (
            <Button size="sm" onClick={handleApplySelected} disabled={applying}
              className={`w-full h-7 text-[10px] font-semibold text-white ${
                domain === 'fabrication' ? 'bg-orange-600 hover:bg-orange-500' : 'bg-violet-600 hover:bg-violet-500'
              }`}
              data-testid={`batch-${domain}-apply-selected-btn`}>
              {applying ? 'Applying...' : `Apply ${selected.size} to ${makeLines.length} Part${makeLines.length !== 1 ? 's' : ''}`}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Template Apply Section                                              */
/* ------------------------------------------------------------------ */
const TemplateApplySection = ({ makeLines, bomId, revisionId, onRefresh }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [applying, setApplying] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!expanded) return;
    (async () => {
      setLoading(true);
      const tk = getToken();
      try {
        const res = await fetchWithAuth(`${API}/api/v1/process-library/templates?limit=50`, { headers: { Authorization: `Bearer ${tk}` } });
        if (res.ok) { const d = await res.json(); setTemplates(d.items || []); }
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, [expanded]);

  const filtered = templates.filter(t =>
    t.template_name?.toLowerCase().includes(search.toLowerCase()) ||
    t.template_code?.toLowerCase().includes(search.toLowerCase())
  );

  const handleApplyTemplate = async (template) => {
    if (makeLines.length === 0) { toast.error('No Make parts selected'); return; }
    setApplying(template.template_id);
    const tk = getToken();
    try {
      const res = await fetchWithAuth(`${API}/api/v1/process-library/templates/${template.template_id}/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
        body: JSON.stringify({
          bom_id: bomId,
          revision_id: revisionId,
          line_ids: makeLines.map(l => l.line_id),
        }),
      });
      if (res.ok) {
        const result = await res.json();
        toast.success(`Template "${template.template_name}" applied to ${result.applied} line(s)`);
        await onRefresh();
      } else {
        const e = await res.json();
        toast.error(e.detail || 'Apply failed');
      }
    } catch { toast.error('Apply failed'); }
    setApplying(null);
  };

  return (
    <div className="border border-cyan-700/30 rounded-lg p-3 bg-cyan-900/10">
      <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="text-[10px] text-cyan-400 font-semibold uppercase tracking-wider flex items-center gap-1">
          <Layers className="w-3 h-3" /> Apply Process Template
          <span className="text-cyan-300/60 ml-1">({makeLines.length} parts)</span>
        </div>
        {expanded ? <ChevronUp className="w-3 h-3 text-slate-500" /> : <ChevronDown className="w-3 h-3 text-slate-500" />}
      </div>
      {expanded && (
        <div className="mt-2 space-y-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-500" />
            <Input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search templates..."
              className="h-7 text-[10px] bg-slate-800 border-slate-600 text-white pl-7" />
          </div>
          <div className="max-h-[200px] overflow-y-auto space-y-1">
            {loading ? (
              <div className="text-[10px] text-slate-500 text-center py-3">Loading templates...</div>
            ) : filtered.length === 0 ? (
              <div className="text-[10px] text-slate-500 text-center py-3">
                {templates.length === 0 ? 'No templates yet. Create one from the Process Library.' : 'No matching templates'}
              </div>
            ) : (
              filtered.map(t => (
                <button key={t.template_id} onClick={() => handleApplyTemplate(t)}
                  disabled={applying === t.template_id}
                  className="w-full text-left px-2 py-1.5 rounded hover:bg-cyan-900/30 transition-colors disabled:opacity-50 border border-transparent hover:border-cyan-700/20"
                  data-testid={`batch-tmpl-${t.template_id}`}>
                  <div className="text-[11px] text-slate-200 font-medium">{t.template_name}</div>
                  <div className="text-[9px] text-slate-500 flex items-center gap-2">
                    <span className="text-orange-400/60">{t.fabrication_process_codes?.length || 0} fab</span>
                    <span className="text-violet-400/60">{t.surface_treatment_codes?.length || 0} sft</span>
                    {t.applicable_material_types?.length > 0 && (
                      <span className="text-cyan-400/60">{t.applicable_material_types.join(', ')}</span>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Main Batch Detail Panel                                             */
/* ------------------------------------------------------------------ */
const BatchDetailPanel = ({ selectedLineIds, lines, profiles, bomId, revisionId, onRefresh, isDraft, onClearSelection, onBusy }) => {
  const [applyingRM, setApplyingRM] = useState(false);
  const [batchProfileId, setBatchProfileId] = useState('');
  const [showRMSection, setShowRMSection] = useState(true);
  const [removing, setRemoving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const selectedLines = useMemo(
    () => lines.filter(l => selectedLineIds.has(l.line_id)),
    [selectedLineIds, lines]
  );

  const makeLines = selectedLines.filter(l => l.make_buy === 'Make' && !l.is_assembly);
  const makeCount = makeLines.length;

  // Bulk apply RM — single API call
  const handleBatchApplyRM = async () => {
    if (!batchProfileId) { toast.error('Select a raw material profile first'); return; }
    if (makeCount === 0) { toast.error('No Make parts selected'); return; }
    setApplyingRM(true);
    const tk = getToken();
    try {
      const res = await fetchWithAuth(
        `${API}/api/v1/bom/${bomId}/revisions/${revisionId}/batch-apply-rm`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
          body: JSON.stringify({
            line_ids: makeLines.map(l => l.line_id),
            material_profile_id: batchProfileId,
          }),
        }
      );
      if (res.ok) {
        const result = await res.json();
        toast.success(`Raw material applied to ${result.applied} part(s)`);
        await onRefresh();
      } else {
        const e = await res.json();
        toast.error(e.detail || 'Apply failed');
      }
    } catch { toast.error('Apply failed'); }
    setApplyingRM(false);
  };

  // Bulk remove lines — single API call
  const handleBatchRemove = async () => {
    setRemoving(true);
    const tk = getToken();
    try {
      const res = await fetchWithAuth(
        `${API}/api/v1/bom/${bomId}/revisions/${revisionId}/batch-remove-lines`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
          body: JSON.stringify({ line_ids: [...selectedLineIds] }),
        }
      );
      if (res.ok) {
        const result = await res.json();
        toast.success(`Removed ${result.count} line(s)`);
        onClearSelection();
        await onRefresh();
      } else {
        const e = await res.json();
        toast.error(e.detail || 'Remove failed');
      }
    } catch { toast.error('Remove failed'); }
    setRemoving(false);
    setConfirmRemove(false);
  };

  return (
    <div className="h-full overflow-y-auto p-3 space-y-3" data-testid="batch-detail-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CheckSquare className="w-4 h-4 text-cyan-400" />
          <span className="text-sm font-semibold text-white">{selectedLineIds.size} Selected</span>
        </div>
        <Button variant="ghost" size="sm" onClick={onClearSelection}
          className="h-6 text-[10px] text-slate-400 hover:text-white"
          data-testid="batch-clear-selection">
          <X className="w-3 h-3 mr-1" /> Clear
        </Button>
      </div>

      {/* Selected Lines Summary */}
      <div className="border border-slate-700/40 rounded-lg p-2 bg-slate-800/30 max-h-[160px] overflow-y-auto">
        <div className="text-[9px] text-slate-500 uppercase tracking-wider mb-1 font-semibold">Selected Parts</div>
        {selectedLines.map(line => (
          <div key={line.line_id} className="flex items-center gap-1.5 py-0.5 text-[10px]">
            <span className={`w-1.5 h-1.5 rounded-sm shrink-0 ${
              line.make_buy === 'Make' ? 'bg-blue-500' : line.make_buy === 'Buy' ? 'bg-emerald-500' : 'bg-amber-500'
            }`} />
            <span className="font-mono text-slate-300 truncate">
              {line.is_assembly ? line.assembly_number : line.part_number}
            </span>
            <span className="text-slate-600 truncate flex-1">{line.part_description}</span>
            <span className="text-[9px] text-slate-600">{line.make_buy}</span>
          </div>
        ))}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="border border-blue-700/30 rounded p-1.5 bg-blue-900/10">
          <div className="text-[10px] text-blue-400 font-mono font-bold">{makeCount}</div>
          <div className="text-[8px] text-slate-500 uppercase">Make</div>
        </div>
        <div className="border border-emerald-700/30 rounded p-1.5 bg-emerald-900/10">
          <div className="text-[10px] text-emerald-400 font-mono font-bold">{selectedLines.filter(l => l.make_buy === 'Buy').length}</div>
          <div className="text-[8px] text-slate-500 uppercase">Buy</div>
        </div>
        <div className="border border-slate-700/30 rounded p-1.5 bg-slate-800/20">
          <div className="text-[10px] text-slate-300 font-mono font-bold">{selectedLineIds.size}</div>
          <div className="text-[8px] text-slate-500 uppercase">Total</div>
        </div>
      </div>

      {/* Batch Actions for Make parts */}
      {makeCount > 0 && isDraft && (
        <>
          {/* Apply Raw Material */}
          <div className="border border-amber-700/30 rounded-lg p-3 bg-amber-900/10">
            <div className="flex items-center justify-between cursor-pointer" onClick={() => setShowRMSection(!showRMSection)}>
              <div className="text-[10px] text-amber-400 font-semibold uppercase tracking-wider flex items-center gap-1">
                <Package className="w-3 h-3" /> Apply Raw Material
                <span className="text-amber-300/60 ml-1">({makeCount} parts)</span>
              </div>
              {showRMSection ? <ChevronUp className="w-3 h-3 text-slate-500" /> : <ChevronDown className="w-3 h-3 text-slate-500" />}
            </div>
            {showRMSection && (
              <div className="mt-2 space-y-2">
                <Label className="text-[10px] text-slate-400 block">Select material for all {makeCount} Make parts:</Label>
                <RMLibrarySelector
                  value={batchProfileId}
                  onChange={(code) => setBatchProfileId(code)}
                />
                <Button size="sm" onClick={handleBatchApplyRM} disabled={!batchProfileId || applyingRM}
                  className="w-full h-7 text-[10px] bg-amber-600 hover:bg-amber-500 text-white"
                  data-testid="batch-apply-rm-btn">
                  {applyingRM ? 'Applying...' : `Apply to ${makeCount} Make Part${makeCount > 1 ? 's' : ''}`}
                </Button>
              </div>
            )}
          </div>

          {/* Add Fabrication Operation */}
          <ProcessSearchSection
            domain="fabrication"
            icon={<Wrench className="w-3 h-3" />}
            title="Add Fabrication Operation"
            color="text-orange-400"
            borderColor="border-orange-700/30"
            bgColor="bg-orange-900/10"
            makeLines={makeLines}
            bomId={bomId}
            revisionId={revisionId}
            onRefresh={onRefresh}
            onBusy={onBusy}
          />

          {/* Add Surface Treatment */}
          <ProcessSearchSection
            domain="surface_treatment"
            icon={<Droplets className="w-3 h-3" />}
            title="Add Surface Treatment"
            color="text-violet-400"
            borderColor="border-violet-700/30"
            bgColor="bg-violet-900/10"
            makeLines={makeLines}
            bomId={bomId}
            revisionId={revisionId}
            onRefresh={onRefresh}
            onBusy={onBusy}
          />

          {/* Apply Process Template */}
          <TemplateApplySection
            makeLines={makeLines}
            bomId={bomId}
            revisionId={revisionId}
            onRefresh={onRefresh}
          />
        </>
      )}

      {makeCount === 0 && (
        <div className="text-[10px] text-slate-500 text-center py-4 border border-dashed border-slate-700/40 rounded">
          No Make parts in selection. Select Make parts to apply materials and processes.
        </div>
      )}

      {/* Bulk Remove Lines — always available for draft */}
      {isDraft && selectedLineIds.size > 0 && (
        <div className="border border-red-800/30 rounded-lg p-3 bg-red-900/10">
          {!confirmRemove ? (
            <button onClick={() => setConfirmRemove(true)}
              className="w-full flex items-center justify-center gap-1.5 py-1.5 text-[10px] text-red-400 font-semibold hover:text-red-300 transition-colors"
              data-testid="batch-remove-btn">
              <Trash2 className="w-3 h-3" /> Remove {selectedLineIds.size} Line{selectedLineIds.size > 1 ? 's' : ''}
            </button>
          ) : (
            <div className="space-y-2">
              <div className="text-[10px] text-red-300 text-center font-semibold">
                Remove {selectedLineIds.size} line{selectedLineIds.size > 1 ? 's' : ''}? This cannot be undone.
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleBatchRemove} disabled={removing}
                  className="flex-1 h-7 text-[10px] bg-red-700 hover:bg-red-600 text-white"
                  data-testid="batch-remove-confirm">
                  {removing ? 'Removing...' : 'Confirm Remove'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setConfirmRemove(false)}
                  className="h-7 text-[10px] border-slate-600 text-slate-300"
                  data-testid="batch-remove-cancel">
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BatchDetailPanel;
